// @flow
import React, { createContext, useMemo, useContext } from 'react';
import { useReducerAsync } from 'use-reducer-async';
import type { State } from './form-submitter-type';
import reducer from './form-submitter-reducer';
import selector from './form-submitter-selector';
import actions from './form-submitter-action';

type Props = {
  children: any,
  components: Object,
  folderId: string,
  form: Object,
  frUrlId: string,
  isAuthenticationRequired: boolean,
  isPreview: boolean,
  onInvalidFormError: Function,
  onSuccessfulSubmission: Function,
};

const defaultState: State = {
  frUrlId: '',
  form: {},
  folderId: '0',
  disabled: false,
  hasChange: false,
  itemQueues: {},
  submitInProgress: false,
  submissionFailed: false,
  isUploadManagerExpanded: false,
  isResumableUploadsEnabled: true,
  isUploadsManagerVisible: false,
  processingInProgress: false,
  response: {},
  isInvalid: false,
  isPreview: false,
  isAuthenticationRequired: false,
  errorNotificationMessage: null,
  components: {},
  uploader: null,
  values: {},
  errors: {},
  validateOnChange: false,
};

const FormSubmitterContext: any = createContext();

const FormSubmitterProvider = ({
  form,
  folderId,
  frUrlId,
  isAuthenticationRequired,
  isPreview,
  components,
  onSuccessfulSubmission,
  onInvalidFormError,
  children,
}: Props) => {
  const initialState = {
    ...defaultState,
    form,
    folderId,
    frUrlId,
    components,
    isPreview,
    isAuthenticationRequired,
  };

  const actionsDispatch = useMemo(
    () => actions({ onSuccessfulSubmission, onInvalidFormError }),
    [onSuccessfulSubmission, onInvalidFormError],
  );
  const [state, dispatch] = useReducerAsync(
    reducer,
    initialState,
    actionsDispatch,
  );
  return (
    <FormSubmitterContext.Provider
      value={{
        state,
        dispatch,
        selector,
      }}
    >
      {children}
    </FormSubmitterContext.Provider>
  );
};

const useFormSubmitterContext = () => {
  const context = useContext(FormSubmitterContext);

  if (!context) {
    throw new Error(
      'useFormSubmitterContext must be used within a FormSubmitterProvider',
    );
  }

  return context;
};

export { FormSubmitterProvider, useFormSubmitterContext, FormSubmitterContext };
